import React, { useState, useRef, useEffect } from 'react';
import { PARAM_DEFS } from '../../../constants/genArt';
import { ToggleSwitch } from '../../../sdk';

/**
 * Compact instrument-style slider with fixed-width LCD value badge.
 * Recessed container, label on left, LCD value on right, round knob.
 * Reset dot appears next to badge when value ≠ default — click to snap back.
 *
 * When `editable` is true, clicking the LCD value opens an inline number input
 * for direct entry (useful for large ranges like Normie ID 1–10000).
 */
function InstrumentSlider({ label, min, max, step, value, defaultValue, onChange, format, editable }) {
  const displayValue = format ? format(value) : (step >= 1 ? `${Math.round(value)}` : value.toFixed(1));
  const isModified = defaultValue !== undefined && Math.abs(value - defaultValue) > (step * 0.1);

  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commitEdit = () => {
    setEditing(false);
    const num = parseFloat(editText);
    if (!isNaN(num)) {
      const clamped = Math.min(max, Math.max(min, Math.round(num / step) * step));
      onChange(clamped);
    }
  };

  const handleValueClick = () => {
    if (!editable) return;
    setEditText(step >= 1 ? `${Math.round(value)}` : value.toFixed(1));
    setEditing(true);
  };

  return (
    <div className="instrument-slider">
      <div className="instrument-slider-header">
        <span className="instrument-slider-label">{label}</span>
        <div className="instrument-slider-displays">
          <div
            className={`instrument-slider-badge instrument-slider-badge-reset${isModified ? ' instrument-slider-badge-modified' : ''}`}
            onClick={isModified ? () => onChange(defaultValue) : undefined}
            title={isModified ? `Reset to ${format ? format(defaultValue) : defaultValue}` : undefined}
          >
            <span className="instrument-slider-reset-icon">
              <svg width="8" height="8" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
              </svg>
            </span>
          </div>
          <div
            className={`instrument-slider-badge instrument-slider-badge-value${editable ? ' cursor-pointer' : ''}`}
            onClick={handleValueClick}
            title={editable ? 'Click to type a value' : undefined}
          >
            {editing ? (
              <input
                ref={inputRef}
                type="number"
                min={min}
                max={max}
                step={step}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onBlur={commitEdit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitEdit();
                  if (e.key === 'Escape') setEditing(false);
                }}
                className="instrument-slider-edit-input"
              />
            ) : (
              <span className="instrument-slider-value">{displayValue}</span>
            )}
          </div>
        </div>
      </div>
      <input
        type="range"
        className="instrument-slider-range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
    </div>
  );
}

/**
 * Dynamic per-generator controls — sliders change based on selected generator type.
 * Also includes global palette modifiers (hue shift, saturation, brightness).
 *
 * Two-column layout: generation parameters (left) + color settings (right).
 * Binary params (min:0, max:1, step:1) render as ToggleSwitch instead of slider.
 */
export function GenArtControls({
  type,
  params,
  hueShift,
  saturation,
  brightness,
  onParamChange,
  onGlobalChange,
  hideColor = false,
}) {
  const paramDefs = PARAM_DEFS[type] || [];

  // Separate binary from continuous params
  const binaryParams = paramDefs.filter(def => def.min === 0 && def.max === 1 && def.step === 1);
  const sliderParams = paramDefs.filter(def => !(def.min === 0 && def.max === 1 && def.step === 1));

  return (
    <div className="space-y-3">
      {/* Binary toggles (full width, above columns) */}
      {binaryParams.length > 0 && (
        <div className="space-y-1">
          {binaryParams.map(def => (
            <div key={def.key} className="flex items-center justify-between py-0.5">
              <span className="text-[10px] text-white/45 uppercase tracking-wider"
                    style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: '9px', letterSpacing: '0.5px' }}>
                {def.label}
              </span>
              <ToggleSwitch
                checked={(params[def.key] ?? def.default) > 0.5}
                onChange={(v) => onParamChange(def.key, v ? 1 : 0)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Layout: two-column grid (normal) or single full-width column (hideColor) */}
      <div className={hideColor ? '' : 'genart-controls-grid'}>
        {/* Parameters column — full width when hideColor */}
        <div className={hideColor ? '' : 'genart-controls-col'}>
          {!hideColor && <div className="genart-controls-col-label">Parameters</div>}
          {sliderParams.length > 0 ? (
            sliderParams.map(def => (
              <InstrumentSlider
                key={def.key}
                label={def.label}
                min={def.min}
                max={def.max}
                step={def.step}
                value={params[def.key] ?? def.default}
                defaultValue={def.default}
                onChange={(v) => onParamChange(def.key, v)}
                format={(v) => def.step >= 1 ? `${Math.round(v)}` : v.toFixed(1)}
                editable={def.editable}
              />
            ))
          ) : (
            <div className="instrument-slider" style={{ opacity: 0.3 }}>
              <div className="instrument-slider-header">
                <span className="instrument-slider-label">None</span>
                <div className="instrument-slider-displays">
                  <div className="instrument-slider-badge instrument-slider-badge-reset">
                    <span className="instrument-slider-reset-icon">
                      <svg width="8" height="8" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
                      </svg>
                    </span>
                  </div>
                  <div className="instrument-slider-badge instrument-slider-badge-value">
                    <span className="instrument-slider-value">—</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Color column — hidden for normies */}
        {!hideColor && (
          <div className="genart-controls-col">
            <div className="genart-controls-col-label">Color</div>
            <InstrumentSlider
              label="Hue Shift"
              min={0}
              max={360}
              step={5}
              value={hueShift}
              defaultValue={0}
              onChange={(v) => onGlobalChange('hueShift', v)}
              format={(v) => `${v}°`}
            />
            <InstrumentSlider
              label="Saturation"
              min={0}
              max={200}
              step={5}
              value={saturation}
              defaultValue={100}
              onChange={(v) => onGlobalChange('saturation', v)}
              format={(v) => `${v}%`}
            />
            <InstrumentSlider
              label="Brightness"
              min={50}
              max={150}
              step={5}
              value={brightness}
              defaultValue={100}
              onChange={(v) => onGlobalChange('brightness', v)}
              format={(v) => `${v}%`}
            />
          </div>
        )}
      </div>
    </div>
  );
}
